import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ConsentCheckbox from "@/components/ConsentCheckbox";
import { Disclaimer } from "@/components/Shared";
import { WizardProgress, FieldGrid, WizardNav, validateStep, PortalHero } from "@/components/pathways/FormBits";
import { Gift, CheckCircle2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { title: "Your details", fields: [
    { key: "full_name", label: "Full name", required: true },
    { key: "email", label: "Email", type: "email", required: true },
    { key: "phone", label: "Mobile number", required: true },
    { key: "whatsapp", label: "WhatsApp (if different)" },
    { key: "country", label: "Country", required: true },
    { key: "relationship", label: "Your relationship with us", type: "select", required: true,
      options: ["Previous Client", "Current Client", "Student", "Parent", "Friend", "Education Agent", "Migration Agent", "Business Partner", "Community Member", "Other"] },
  ]},
  { title: "Your friend's details", fields: [
    { key: "friend_name", label: "Friend's full name", required: true },
    { key: "destination_country", label: "Country your friend is interested in", type: "select", required: true, options: ["Australia", "New Zealand", "Both / Not sure"] },
    { key: "friend_country", label: "Friend's country", required: true },
    { key: "friend_nationality", label: "Friend's nationality" },
    { key: "friend_email", label: "Friend's email (optional)", type: "email" },
    { key: "friend_phone", label: "Friend's phone / WhatsApp", required: true },
    { key: "friend_contact_method", label: "Preferred contact method", type: "select", options: ["Phone", "WhatsApp", "Email"] },
  ]},
  { title: "What does your friend need?", fields: [
    { key: "services", label: "Select one or more", type: "multi", required: true,
      options: ["Student Visa", "Visitor Visa", "Skilled Migration", "Employer Sponsorship", "Partner Visa", "Parent Visa", "Permanent Residency", "University Admission", "OSHC / Insurance", "Not Sure"] },
  ]},
  { title: "Additional information & consent", fields: [
    { key: "details", label: "Tell us anything that will help us understand your friend's situation", type: "textarea",
      placeholder: "e.g. current visa · occupation · preferred course · English level · budget · timeframe…" },
  ]},
];

export default function ReferFriend() {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [consent, setConsent] = useState(false);
  const [friendPermission, setFriendPermission] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const isLast = step === STEPS.length - 1;
  const onChange = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const top = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const next = async () => {
    const missing = validateStep(STEPS[step].fields, values);
    if (missing) { toast.error(`Please complete: ${missing}`); return; }
    if (!isLast) { setStep(step + 1); top(); return; }
    if (!friendPermission) { toast.error("Please confirm your friend has given permission to be contacted."); return; }
    if (!consent) { toast.error("Please tick the consent checkbox to submit."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/referral/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, friend_permission: true, consent: true, consent_timestamp: new Date().toISOString() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Submission failed");
      setResult(data);
      toast.success("Referral received — thank you!");
      top();
    } catch (err) { toast.error(err.message || "Something went wrong — please try again."); }
    finally { setLoading(false); }
  };

  if (result) {
    return (
      <div data-testid="referral-result" className="bg-white">
        <PortalHero icon={CheckCircle2} eyebrow="Referral received" title="Thank you for your referral!" subtitle="Your referral has been successfully received and our team has been notified." />
        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#F8F4FB] border-l-4 border-[#29324A] p-6">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#29324A]">Your Referral ID</div>
              <div className="font-display text-3xl text-[#29324A] mt-1" data-testid="referral-reference">{result.reference_id}</div>
            </div>
            <div className="mt-6 bg-white border border-slate-200 p-6 space-y-3 text-[14px] text-slate-700 leading-relaxed">
              <p>Our team will contact <strong>{values.friend_name}</strong> shortly.</p>
              <p>If your referral becomes our client, you may be eligible for referral rewards in accordance with our Referral Program.</p>
              <p className="text-[13px] text-slate-500">A confirmation email has been sent to <strong>{values.email}</strong>.</p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button type="button" data-testid="refer-another-btn" onClick={() => { setResult(null); setValues({}); setStep(0); setConsent(false); setFriendPermission(false); top(); }}
                className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4">
                Refer Another Friend <ArrowRight className="h-4 w-4" />
              </button>
              <Link to="/" className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#29324A] text-[#29324A] text-xs font-bold tracking-[0.2em] uppercase px-6 py-4">Return Home</Link>
            </div>
          </div>
        </section>
        <Disclaimer />
      </div>
    );
  }

  return (
    <div data-testid="refer-friend-page" className="bg-white">
      <PortalHero
        icon={Gift}
        eyebrow="Referral Rewards Program"
        title="Help your friends achieve their Australian or New Zealand dream — and get rewarded."
        subtitle="Whether you're a student, former client, education agent, business partner or simply someone who knows a person planning to come to Australia or New Zealand, we'd love to hear from you."
      />
      <section className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-slate-200 shadow-sm border-l-4 border-l-[#C43A89] p-6 md:p-8">
            <WizardProgress titles={STEPS.map((s) => s.title)} current={step} />
            <FieldGrid fields={STEPS[step].fields} values={values} onChange={onChange} />
            {isLast && (
              <div className="mt-6">
                <label className="flex items-start gap-3 text-xs text-slate-600 leading-relaxed cursor-pointer">
                  <input type="checkbox" checked={friendPermission} onChange={(e) => setFriendPermission(e.target.checked)} data-testid="friend-permission-checkbox" className="mt-0.5 h-4 w-4 accent-[#29324A] shrink-0" />
                  <span>I confirm that my friend has given permission for us to contact them.</span>
                </label>
                <ConsentCheckbox value={consent} onChange={setConsent} testid="referral-consent" />
              </div>
            )}
            <WizardNav onBack={() => { setStep(Math.max(0, step - 1)); top(); }} onNext={next}
              nextLabel={isLast ? "Submit Referral" : "Continue"} loading={loading} showBack={step > 0} />
          </div>
        </div>
      </section>
      <Disclaimer />
    </div>
  );
}
